import { format } from "date-fns";
import { isBrowser } from "../helpers/common";
import { ApiResponse, err, getAxios, postAxios, processError, res } from "./axios";

export const isLoggedIn = async (uid?: number):Promise<boolean> => {
  const token = isBrowser() && window.localStorage.getItem("token");

  if (!token) return false;

  try {
    const result = await getAxios(`/api/v1/auth/is-logged-in`, false)
    const response = result?.data?.loggedIn || null
    return (response && response === true)
  } catch {
    return false
  }
}

interface CreateUserProps {
  first_name: string | null
  last_name: string | null
  email: string | null
  password: string | null
  user_organisation: string | null
  job_title: string | null
}

export const createUser = async ({
  first_name, 
  last_name, 
  email, 
  password,
  user_organisation, 
  job_title, 
}: CreateUserProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/create-user`, {
      first_name,
      last_name,
      email,
      password,
      user_organisation, 
      job_title,     
    }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error creating user account.")
    return err(message, status)
  }
}

export const logout = () => {
  isBrowser() && window.localStorage.removeItem("token");
};

interface LoginProps {
  email: string | null
  password: string | null
  rememberMe: boolean
  downgrade?: boolean
  device_token?: string   // set automatically below; callers don't pass it
}

export const login = async ({
  email,
  password,
  rememberMe,
  downgrade,
  device_token,
}: LoginProps) => {
  try {
    const result = await postAxios(`/api/v1/auth/login`, {
      email,
      password,
      rememberMe,
      downgrade,
      device_token: isBrowser() ? localStorage.getItem("device_token") : device_token,
    }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error logging in, please try again or reach out to support.")
    return err(message, status)
  }
};

interface ResetPasswordProps {
  email: string
}

export const resetPassword = async ({ email }: ResetPasswordProps) => {
  try {
    const result = await postAxios(`/api/v1/auth/reset-password`, { email }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error resetting password.")
    return err(message, status)
  }
};

export const resendConfirmationEmail = async ({ email }: ResetPasswordProps) => {
  try {
    const result = await postAxios(`/api/v1/auth/resend-confirmation-email`, { email }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error generating confirmation email")
    return err(message, status)
  }
};


export const confirmEmail = async ({ hash }: {
  hash: string
}) => {
  try {
    const result = await postAxios(`/api/v1/auth/confirm-email`, { hash }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error confirming email")
    return err(message, status)
  }
};


export const getUserDetails = async ():Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/auth/get-user`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error getting user details.")
    return err(message, status)
  }
}

export interface SetNewResetPasswordRequest {
  token?: string | undefined | null;
  password: string;
  first_name?: string;
  last_name?: string;
}

export const setNewResetPassword = async ({ token, password, first_name, last_name }: SetNewResetPasswordRequest) => {
  try {
    const result = await postAxios("/api/v1/auth/set-new-reset-password", {
      token,
      password,
      first_name,
      last_name,
    }, false);
    const response = result?.data || null
    return res(response)
  } catch (error: any) {
    const {status, message} = processError(error, "Error set new user password.")
    return err(message, status)
  }
};



export interface UpdatePasswordRequest {
  password: string;
  existing_password: string
}

export const updateUserPassword = async ({ password, existing_password }: UpdatePasswordRequest) => {
  try {
    const result = await postAxios("/api/v1/auth/update-user-password", {
      password,
      existing_password,
    }, false);
    const response = result?.data || null
    return res(response)
  } catch (error: any) {
    const {status, message} = processError(error, "Error updating user password.")
    return err(message, status)
  }
};


interface UpdateUserAccountProps {
  first_name: string | null
  last_name: string | null
  email: string | null
  notifications?: boolean | null
  userId?: number | string | null
  enable_2fa?: boolean | null
}

export const updateUserAccount = async ({first_name, last_name, email, notifications, userId, enable_2fa}: UpdateUserAccountProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/update-user-account`, {
      user_id: userId || null,
      first_name,
      last_name,
      email,
      notifications,
      enable_2fa,
    }, false)

    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error updating user details.")
    return err(message, status)
  }
}

interface GetUserProjectsAndItemsProps {
  lat?: number | null | undefined
  lon?: number | null | undefined
}

export const getUserProjectsAndItems = async ({
  lat,
  lon,
}: GetUserProjectsAndItemsProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/get-user-projects-and-items`, {
      lat, lon,
    }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting user project items.")
    return err(message, status)
  }
}

export const getAllUserCustomData = async ():Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/get-user-custom-data`, {}, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting user custom data items.")
    return err(message, status)
  }
}

export const getUserProjects = async ():Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/auth/get-user-projects`, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting user projects.")
    return err(message, status)
  }
}


export const getUserApiTokens = async ():Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/auth/get-user-api-tokens`, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting user api tokens.")
    return err(message, status)
  }
}



interface DeleteUserApiTokenProps {
  id?: number | string
}

export const deleteUserApiToken = async ({id}: DeleteUserApiTokenProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/delete-user-api-token`, {
      id,
    }, false)

    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error deleting user API token.")
    return err(message, status)
  }
}

interface CreateUserApiTokenProps {
  expiry_at?: string | null
  full_access?: boolean | null
  company_id?: number | string | null
  use_expiry_date?: boolean | null
}

export const createUserApiToken = async ({expiry_at, use_expiry_date, full_access, company_id}: CreateUserApiTokenProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/create-user-api-token`, {
      expiry_at: (use_expiry_date && expiry_at) ? format( new Date(expiry_at), 'yyyy-MM-dd') : null, 
      full_access, 
      company_id: full_access ? (Number(company_id) || null) : null,
    }, false)

    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error creating user API token.")
    return err(message, status)
  }
}

interface UpdateUserItemProps {
  order_item_id?: number | string
  existing_project_id?: number | string | null
  new_project_id?: number | string | null
  name: string | null
}

export const updateUserItem = async ({order_item_id, existing_project_id, new_project_id, name}: UpdateUserItemProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/update-user-project-item`, {
      order_item_id,
      existing_project_id,
      new_project_id,
      name
    }, false)

    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error updating user details.")
    return err(message, status)
  }
}

interface UpdateProjectProps {
  id?: number | string | null
  organisation_id?: number | string | null
  name: string | null
  description: string | null
}

export const updateProject = async ({id, organisation_id, name, description}: UpdateProjectProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/update-project`, {
      id,
      organisation_id,
      name,
      description
    }, false)

    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error updating project.")
    return err(message, status)
  }
}



interface DeleteProjectProps {
  id?: number | string
}

export const deleteProject = async ({id}: DeleteProjectProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/delete-project`, {
      id,
    }, false)

    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error deleting project.")
    return err(message, status)
  }
}


export const getTokenHistory = async ({
  tokenId,
}: {
  tokenId: string | number | null
}):Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/auth/get-tokens-history/${tokenId}`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error retrieving tokens history.")
    return err(message, status)
  }
}

export const getTempToken = async ():Promise<string | null> => {
  try {
    const result = await postAxios(`/api/v1/auth/generate-temp-token`, {}, false)
    const res = result?.data || null
    if (res && res.response?.token) {
      return res.response?.token
    }
    return null
  } catch (e) {
    // const {status, message} = processError(e, "Error getting temp token.")
    return null
  }
}

export const regenerateUserApiToken = async ({
  id,
}: {
  id: string | number | null
}):Promise<ApiResponse> => {
  try {
    const result = await getAxios(`/api/v1/auth/regenerate-user-token/${id}`, false)
    const response = result?.data || null
    return res(response)

  } catch (e) {
    const {status, message} = processError(e, "Error generating new token.")
    return err(message, status)
  }
}

export const verifyTwoFactor = async ({
  code,
  token,
}: {
  code: string;
  token: string;
}):Promise<ApiResponse> => {
  try {
    isBrowser() && window.localStorage.setItem("token", token);

    const result = await postAxios(`/api/v1/auth/verify-2fa`, {
      code
    }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error authenticating 2FA token.")
    return err(message, status)
  }
}

export const switchToStandardView = async ({ }) => {
  try {
    const result = await postAxios(`/api/v1/auth/switch-view`, { }, false) // authenticated
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const { status, message } = processError(e, "Error switching view, please try again.")
    return err(message, status)
  }
};

export const resendTwoFactorCode = async ({ token }: { token: string }) => {
  // export const resendTwoFactorCode = async () => {
  try {
    isBrowser() && window.localStorage.setItem("token", token);

    // const result = await postAxios(
    //   `/api/v1/auth/resend-2fa-code`,
    //   {},
    //   false,
    //   { Authorization: `Bearer ${token}` } // challenge token — match your verifyTwoFactor call exactly
    // )
    const result = await postAxios(`/api/v1/auth/resend-2fa-code`, { }, true) // authenticated

    const response = result?.data || null
    return res(response)
  } catch (e) {
    const { status, message } = processError(e, "Couldn't resend the code, please try again.")
    return err(message, status)
  }
};

export const deleteOwnAccount = async ({
  password,
}: {
  password: string;
}): Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/auth/delete-account`, { password }, false);
    const response = result?.data || null;
    return res(response);
  } catch (e) {
    const { status, message } = processError(e, "Error deleting account.");
    return err(message, status);
  }
};